import { Color } from '@persona-id/design-tokens';
import { sumBy } from 'lodash';
import {
  forwardRef,
  Fragment,
  MutableRefObject,
  ReactNode,
  Ref,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { InquiryFunnelStepType } from 'dashboard/models/inquiry-funnel';
import { WORKFLOW_STEP_CARD_HEIGHT } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelStepCard';
import { FunnelNode } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelTree';
import {
  buildStepHeights,
  CONNECTOR_BORDER_WIDTH,
  HORIZONTAL_LINE_TOP_MARGIN,
  StepFlowBranchContainer,
  StepFlowBranchEmptySpacer,
  StepFlowBranchStepsContainer,
  StepFlowBranchSubTree,
  StepFlowContainer,
  StepFlowStepContainer,
  StepPosition,
} from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/StepFlowElements';

// Abstract interface of common props between StepFlow and StepAndConnector
interface StepFlowConsistentProps {
  steps: FunnelNode[];
  stepContentHeight: number;
  renderStep: (
    ref: MutableRefObject<HTMLDivElement>,
    step: FunnelNode,
    currentPath: FunnelNode[],
  ) => ReactNode;
  renderConnector: (previousStep: FunnelNode, nextStep: FunnelNode) => ReactNode;
  currentPath: FunnelNode[];
  selectedPath: FunnelNode[];
}

interface StepFlowProps extends StepFlowConsistentProps {
  innerRef: Ref<HTMLDivElement>;
}

interface StepAndConnectorProps extends StepFlowConsistentProps {
  stepTokenToPositionMap: Map<string, StepPosition>;
}

const SANKEY_PATH_CONNECTOR_LENGTH = 100;
const SANKEY_PATH_SINGLE_CONNECTOR_LENGTH = 50;
const CURVE_CONTROL_POINT_X_OFFSET = 25;
const SANKEY_CONNECTOR_MARGIN = 6;

const SankeyPathsContainer = styled.div<{
  branchCount: number;
}>`
  height: ${WORKFLOW_STEP_CARD_HEIGHT}px;
  display: flex;
  flex-direction: column;
  width: ${({ branchCount }) =>
    branchCount > 1 ? SANKEY_PATH_CONNECTOR_LENGTH : SANKEY_PATH_SINGLE_CONNECTOR_LENGTH}px;
  position: relative;
`;

const SankeyPath = styled('div')<{ height: number; transformY: number }>`
  position: absolute;
  top: ${({ transformY }) => transformY}px;
`;

export const MIN_CONNECTOR_HEIGHT = 8;

// A connector connects one node to another.
interface Connector {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  // this is used to account for the delta between the curve width at the destination and the source
  // in situations where we have to make the source smaller than it would've been to give room to
  // siblings that are too small, we have to fill in some of the room at the destination.
  heightDelta: number;
  strokeWidth: number;
}

/**
 * A component to render steps with tree-ish layout
 *
 * @param innerRef - The ref for the root container. Couldn't use React.forwardRef instead to avoid unintuitive Generic wrapping
 * @param steps - The list of steps that will be walked iteratively.
 * Expects each element has an unique `token`, and optional `branches` which has child steps
 * @param stepContentWidth - The width of a single rendered step
 * @param renderStep - The function to render each step that takes `step` as an argument
 * @param renderConnector - The function to render a connector between steps that takes "previous step" and "next step"
 * "next step" will be `undefined` when doesn't exist
 */

const InquiryFunnelStepFlow = <
  Step extends { token: string; pathRatio: number; branches?: Step[][] },
>({
  innerRef,
  steps,
  stepContentHeight,
  renderStep,
  renderConnector,
  currentPath,
  selectedPath,
}: StepFlowProps) => {
  const stepTokenToPositionMap: Map<string, StepPosition> = useMemo(() => {
    const map = new Map<string, StepPosition>();
    buildStepHeights(steps, stepContentHeight, map);
    return map;
  }, [stepContentHeight, steps]);

  return (
    <StepFlowContainer ref={innerRef}>
      <StepAndConnector
        steps={steps}
        stepContentHeight={stepContentHeight}
        stepTokenToPositionMap={stepTokenToPositionMap}
        renderStep={renderStep}
        renderConnector={renderConnector}
        currentPath={currentPath}
        selectedPath={selectedPath}
      />
    </StepFlowContainer>
  );
};

const isUpwardCurve = (connector: Connector) => {
  return connector.toY < connector.fromY;
};

const renderConnectors = (
  currentStep: FunnelNode,
  connectors: Connector[],
  selectedPath: FunnelNode[],
) => {
  const selectedPathNodes = selectedPath.map((path) => path.nodeToken);
  const currentStepBranches = currentStep.branches || [];
  return (
    <>
      {currentStepBranches.map((nextNode, index) => {
        if (connectors.length !== currentStepBranches.length) {
          return null;
        }
        const connector = connectors[index];
        const isUp = isUpwardCurve(connector);
        const curveRadius = Math.min(
          Math.abs((connector.toX - connector.fromX) / 2),
          Math.abs((connector.toY - connector.fromY) / 2),
        );
        const lineEndY = connector.toY + (isUp ? +curveRadius : -curveRadius);
        /*
         * This is where it gets a bit tricky.
         * The paths that we render mostly look like this
         *
         *            /----- node-child-1
         *           /
         * ---- node *
         *           \
         *            \----- node-child-2
         *
         * Where * is 0,0 for our svg canvas.
         * In order to render paths that go up and to the right, we are using negative Y coordinates,
         * which svg is okay with.
         * However, it means that the path is outside of the svg viewport. So to compensate for it,
         * we translate the whole svg down by the height of the top path, and then we position the
         * container div up to bring it back to where it was before, but now everything is visible.
         */
        const transformY = isUp ? -(connector.toY - connector.fromY - connector.strokeWidth) : 0;

        /* Our paths are composed of 3 components
         * a quadratic curve, a straight line, and then another quadratic curve.
         *
         *  ----  &            ----------------------
         *      \              <- quadratic curve 1
         *       \
         *        |            ----------------------
         *        |            <- straight line
         *        |
         *        |
         *         \            ----------------------
         *          \           <- quadratic curve 2
         *        ^  \----       ----------------------
         *
         *
         * Each quadratic curve has a control point, which roughly defines how curvy it is
         * (this is the two numbers following Q in the svg below).
         *
         * When there are multiple curves, they have a tendency to overlap. To prevent overlapping
         * multiple curves, we do one of two things
         *
         * 1. for curves that go up and to the right, we shift the control point by the right by
         * the total cumulative stroke widths so far.
         * i.e. if the first curve is 10px wide, we shift the second curve by ~10px to the right.
         *
         * For curves that go down and to the right, we shift the control point to the right by the
         * total cumulative stroke widths of the left over curves.
         *
         * This way, the curves should be directly adjacent.
         */
        let outboundCurveEndX =
          connector.fromX +
          CURVE_CONTROL_POINT_X_OFFSET +
          (isUp
            ? sumBy(connectors.slice(0, index), (connector) => connector.strokeWidth - 1)
            : sumBy(
                connectors.slice(index + 1, connectors.length),
                (connector) => connector.strokeWidth - 2,
              )) +
          CONNECTOR_BORDER_WIDTH * 4;
        let outboundCurveEndY = connector.fromY;
        if (isUp) {
          outboundCurveEndY -= curveRadius;
          outboundCurveEndX = Math.min(outboundCurveEndX, connector.toX - connector.strokeWidth);
        } else {
          outboundCurveEndY += curveRadius;
        }
        return (
          <SankeyPath key={index} height={WORKFLOW_STEP_CARD_HEIGHT} transformY={-transformY}>
            <svg
              fill='transparent'
              stroke='transparent'
              style={{
                // The height just needs to be large enough to render the whole path.
                // The * 10 here is a bit of a rough guess but it works good enough for now.
                height: `${Math.max(1000, Math.abs(connector.toY - connector.fromY)) * 100}px`,
              }}
            >
              <g transform={`translate(0, ${transformY})`}>
                <path
                  strokeWidth={connector.strokeWidth}
                  d={`M ${connector.fromX} ${connector.fromY}
                    Q ${outboundCurveEndX} ${connector.fromY}
                    ${outboundCurveEndX} ${outboundCurveEndY}
                    V ${lineEndY}
                    Q ${outboundCurveEndX} ${connector.toY + (isUp ? 0 : connector.heightDelta)}
                    ${connector.toX} ${connector.toY + (isUp ? 0 : connector.heightDelta)}
                    `}
                  stroke={
                    selectedPathNodes.includes(nextNode[0].nodeToken)
                      ? Color.CorePurple600
                      : Color.CoreSlate200
                  }
                />
                <g transform={`translate(-${CONNECTOR_BORDER_WIDTH / 2})`}>
                  <path
                    strokeWidth={connector.strokeWidth}
                    stroke={
                      selectedPathNodes.includes(nextNode[0].nodeToken)
                        ? Color.CorePurple800
                        : Color.CoreSlate1000
                    }
                    d={`M ${connector.fromX} ${connector.fromY}
                  H ${connector.fromX + CONNECTOR_BORDER_WIDTH}`}
                  />
                </g>
                <g transform={`translate(${CONNECTOR_BORDER_WIDTH / 2})`}>
                  <path
                    strokeWidth={connector.strokeWidth}
                    stroke={
                      selectedPathNodes.includes(nextNode[0].nodeToken)
                        ? Color.CorePurple800
                        : Color.CoreSlate1000
                    }
                    d={`M ${connector.toX} ${connector.toY + (isUp ? 0 : connector.heightDelta)}
                  H ${connector.toX - CONNECTOR_BORDER_WIDTH}`}
                  />
                </g>
                {connector.heightDelta === 0 ? null : (
                  <>
                    <g
                      transform={`translate(${CONNECTOR_BORDER_WIDTH / 2}, ${
                        -connector.strokeWidth / 2 + connector.heightDelta / 2
                      })`}
                    >
                      <path
                        strokeWidth={connector.heightDelta + 1}
                        stroke={
                          selectedPathNodes.includes(nextNode[0].nodeToken)
                            ? Color.CorePurple800
                            : Color.CoreSlate1000
                        }
                        d={`M ${connector.toX} ${connector.toY + (isUp ? connector.strokeWidth : 0)}
                        H ${connector.toX - CONNECTOR_BORDER_WIDTH}`}
                      />
                      <path
                        strokeWidth={connector.heightDelta}
                        stroke={
                          selectedPathNodes.includes(nextNode[0].nodeToken)
                            ? Color.CorePurple600
                            : Color.CoreSlate200
                        }
                        d={`M ${
                          isUp
                            ? outboundCurveEndX
                            : (outboundCurveEndX + connector.toX) / 2 - connector.heightDelta
                        } ${connector.toY + (isUp ? connector.strokeWidth : 0)}
                  H ${connector.toX - CONNECTOR_BORDER_WIDTH}`}
                      />
                    </g>
                  </>
                )}
              </g>
            </svg>
          </SankeyPath>
        );
      })}
    </>
  );
};

const calculateBranchRatios = (
  inboundRatio: number,
  branches: FunnelNode[][],
): { ratio: number; originalRatio: number }[] => {
  const childRatios = sumBy(branches, (branch) => branch[0].pathRatio);
  // If we have undefined ratios then we evenly split the branches.
  if (isNaN(childRatios)) {
    const equalRatio = 1 / branches.length;
    return branches.map(() => {
      return {
        ratio: equalRatio,
        originalRatio: equalRatio,
      };
    });
  }
  if (childRatios === 0) {
    return branches.map(() => {
      return {
        ratio: 0,
        originalRatio: 0,
      };
    });
  }
  const maxRatio = Math.min(1, childRatios / inboundRatio);
  const equalRatio = maxRatio / branches.length;
  const minimumRatio = Math.min(0.1, equalRatio);
  let totalRatioFudged = 0;
  let nonAdjustedBranches = 0;
  const ratiosAdjustments = branches.map((branch) => {
    const naiveRatio = branch[0].pathRatio / inboundRatio;
    if (naiveRatio < minimumRatio) {
      const fudgedUp = minimumRatio - naiveRatio;
      totalRatioFudged += fudgedUp;
      return fudgedUp;
    }
    nonAdjustedBranches++;
    return 0;
  });

  // totalRatioFudged > 0 means that we made some branches larger than they would've been for display purposes.
  // the extra amount needs to be removed, otherwise we will have used up too much space.
  // we remove the ratio from the non fudged branches.
  return branches.map((branch, index) => {
    const naiveRatio = branch[0].pathRatio / inboundRatio;
    const actualRatio =
      naiveRatio +
      ratiosAdjustments[index] -
      (ratiosAdjustments[index] === 0 ? totalRatioFudged / nonAdjustedBranches : 0);
    return {
      ratio: actualRatio,
      originalRatio: naiveRatio,
    };
  });
};

const calculateStrokeWidth = (
  inboundRatio: number,
  branchRatio: number,
  stepRef: MutableRefObject<HTMLDivElement>,
) => {
  return Math.max(
    MIN_CONNECTOR_HEIGHT,
    inboundRatio * branchRatio * (stepRef.current.offsetHeight - HORIZONTAL_LINE_TOP_MARGIN),
  );
};

const StepAndConnector = forwardRef<HTMLDivElement, StepAndConnectorProps>((props, ref) => {
  const { renderStep, renderConnector, steps, currentPath, selectedPath } = props;
  const [currentStep, ...nextSteps] = steps;

  const connectorAfterStep = () => {
    if (currentStep.type === InquiryFunnelStepType.Conditional) {
      return null;
    }
    return renderConnector(currentStep, nextSteps[0]);
  };

  const currentStepBranches = currentStep.branches ?? [];
  const renderAfterStep = () => {
    return (
      <>
        {(currentStepBranches.length > 0 || nextSteps.length > 0) && connectorAfterStep()}
        {/* next step */}
        {nextSteps.length > 0 && (
          <StepAndConnector
            {...props}
            steps={nextSteps}
            currentPath={[...currentPath, currentStep]}
          />
        )}
      </>
    );
  };

  const stepRef = useRef<HTMLDivElement>(null);
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const childRefs = useRef<(HTMLDivElement | null)[]>(Array(currentStepBranches.length));

  useEffect(() => {
    if (childRefs.current.length === connectors.length) {
      return;
    }
    let heightAcc = HORIZONTAL_LINE_TOP_MARGIN / 2;
    const inboundRatio = isNaN(currentStep.pathRatio) ? 0 : currentStep.pathRatio;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const branchRatios = calculateBranchRatios(inboundRatio, currentStep.branches);
    setConnectors(
      childRefs.current.map((ref, index) => {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        const strokeWidth = calculateStrokeWidth(inboundRatio, branchRatios[index].ratio, stepRef);
        const originalStrokeWidth = calculateStrokeWidth(
          inboundRatio,
          branchRatios[index].originalRatio,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          stepRef,
        );
        const oldHeightAcc = heightAcc;
        heightAcc += strokeWidth;
        const fromY = oldHeightAcc + strokeWidth / 2;
        return {
          fromX: SANKEY_CONNECTOR_MARGIN,
          fromY: fromY,
          toX:
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            ref.offsetLeft -
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            (stepRef.current.offsetLeft + stepRef.current.offsetWidth) -
            SANKEY_CONNECTOR_MARGIN,
          toY:
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            ref.offsetTop -
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            stepRef.current.offsetTop +
            strokeWidth / 2 +
            HORIZONTAL_LINE_TOP_MARGIN / 2,
          strokeWidth,
          heightDelta: originalStrokeWidth > strokeWidth ? originalStrokeWidth - strokeWidth : 0,
        };
      }),
    );
  }, [connectors, currentStep.branches, currentStep.pathRatio]);
  return (
    <StepFlowStepContainer>
      {/* current step */}
      {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
      <div ref={ref}>{renderStep(stepRef, currentStep, currentPath)}</div>
      {currentStepBranches.length > 0 && (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <SankeyPathsContainer branchCount={currentStep.branches.length}>
          {renderConnectors(currentStep, connectors, selectedPath)}
        </SankeyPathsContainer>
      )}
      {/* branches under a current step */}
      {currentStepBranches && currentStepBranches.length > 0 && (
        <StepFlowBranchContainer blur={false}>
          <StepFlowBranchStepsContainer>
            {currentStepBranches.map((branchSteps, branchIndex) => {
              return (
                <Fragment key={branchIndex}>
                  <StepFlowBranchSubTree key={branchIndex}>
                    {branchSteps.length > 0 && (
                      <StepAndConnector
                        {...props}
                        ref={(ref) => {
                          childRefs.current[branchIndex] = ref;
                        }}
                        steps={branchSteps}
                        currentPath={[...currentPath, currentStep]}
                      />
                    )}
                  </StepFlowBranchSubTree>
                  <StepFlowBranchEmptySpacer height={WORKFLOW_STEP_CARD_HEIGHT} />
                </Fragment>
              );
            })}
          </StepFlowBranchStepsContainer>
        </StepFlowBranchContainer>
      )}
      {renderAfterStep()}
    </StepFlowStepContainer>
  );
});

export default InquiryFunnelStepFlow;
